/**
 * Firebase Data Utilities for Cross-Platform Compatibility
 * Thomas Notes: Handles defensive data parsing and platform-aware features
 * Added comprehensive null safety and platform detection for web/mobile compatibility
 */

// Platform constants
export const PLATFORMS = {
  WEB_REACT: 'web_react',
  FLUTTER_ANDROID: 'flutter_android', 
  FLUTTER_IOS: 'flutter_ios',
  SWIFTUI_IOS: 'swiftui_ios',
  UNKNOWN: 'unknown',
};

export const CALL_STATUSES = {
  AVAILABLE: 'available',
  BUSY: 'busy',
  PENDING: 'pending',
  ACTIVE: 'active',
  ENDED: 'ended',
  DECLINED: 'declined',
  REJECTED: 'rejected', // Thomas Notes: Explicit rejection status from mobile apps
  TIMEOUT: 'timeout',
  CANCELLED: 'cancelled', // Thomas Notes: Call cancelled by caller before answer
  EMERGENCY_PENDING: 'emergency_pending',
  EMERGENCY: 'emergency', // Thomas Notes: Active emergency call
  UNKNOWN: 'unknown',
};

export const DISCONNECT_REASONS = {
  USER_LOGOUT: 'user_logout',
  CONNECTION_LOST: 'connection_lost',
  APP_BACKGROUNDED: 'app_backgrounded',
  UNKNOWN: 'unknown',
};

/**
 * Thomas Notes: Safely extract user status data with defaults
 * This prevents crashes when Firebase data is missing or malformed
 * @param {Object} userData - Raw Firebase user data
 * @returns {Object} Normalized user data with defaults
 */
export const normalizeUserData = (userData) => {
  if (!userData || typeof userData !== 'object') {
    return getDefaultUserData();
  }

  return {
    // Thomas Notes: Core status fields with safe defaults
    callStatus: userData.callStatus || CALL_STATUSES.UNKNOWN,
    isOnline: Boolean(userData.is_online),
    lastSeen: userData.last_seen || null,
    updatedAt: userData.updated_at || null,
    
    // Thomas Notes: App metadata for platform identification
    appType: userData.app_type || PLATFORMS.UNKNOWN,
    platform: userData.platform || PLATFORMS.UNKNOWN,
    appVersion: userData.app_version || 'Unknown',
    buildNumber: userData.build_number || 'Unknown',
    deviceInfo: userData.device_info || 'Unknown Device',
    packageName: userData.package_name || 'Unknown',
    
    // Connection info
    disconnectReason: userData.disconnect_reason || null,
    
    // Call-specific fields (optional)
    callId: userData.call_id || null,
    channel: userData.channel || null,
    callDirection: userData.call_direction || null,
    callType: userData.call_type || null,
    remoteUserId: userData.remote_user_id || null,
    
    // Web-specific fields (optional)
    browserName: userData.browser_name || null,
    browserVersion: userData.browser_version || null,
    // #648: the presence writer sets `network_type` (cortex-be
    // PresenceController); `connection_type` was never written by anything,
    // so this field read null for every client on every platform. NOTE the
    // value carries each producer's own vocabulary — the web sends
    // navigator connection.effectiveType ("4g"), Flutter sends an interface
    // type ("wifi"/"cellular"/"ethernet"), and iOS currently hardcodes
    // "unknown" — so any surface that renders it must normalise first.
    connectionType: userData.network_type || null,
    timezone: userData.timezone || null,
    
    // Raw data for debugging
    _raw: userData,
  };
};

/**
 * Get default user data structure
 * @returns {Object} Default user data
 */
export const getDefaultUserData = () => ({
  callStatus: CALL_STATUSES.UNKNOWN,
  isOnline: false,
  lastSeen: null,
  updatedAt: null,
  appType: PLATFORMS.UNKNOWN,
  platform: PLATFORMS.UNKNOWN,
  appVersion: 'Unknown',
  buildNumber: 'Unknown',
  deviceInfo: 'Unknown Device',
  packageName: 'Unknown',
  disconnectReason: null,
  callId: null,
  channel: null,
  callDirection: null,
  callType: null,
  remoteUserId: null,
  browserName: null,
  browserVersion: null,
  connectionType: null,
  timezone: null,
  _raw: {},
});

/**
 * Check if user is available for calls
 * @param {Object} userData - Normalized user data
 * @returns {Object} Availability status with reason
 */
export const checkUserAvailability = (userData) => {
  const normalized = normalizeUserData(userData);
  
  // Check online status
  if (!normalized.isOnline) {
    return {
      available: false,
      reason: 'offline',
      message: `User was last seen ${formatLastSeen(normalized.lastSeen)}`,
      canRetry: false,
    };
  }
  
  // Check call status
  switch (normalized.callStatus) {
    case CALL_STATUSES.AVAILABLE:
      return {
        available: true,
        reason: 'available',
        message: 'User is available for calls',
        canRetry: true,
      };
      
    case CALL_STATUSES.BUSY:
    case CALL_STATUSES.ACTIVE:
      return {
        available: false,
        reason: 'busy',
        message: 'User is currently on another call',
        canRetry: true,
      };
      
    case CALL_STATUSES.PENDING:
      return {
        available: false,
        reason: 'pending',
        message: 'User has an incoming call',
        canRetry: true,
      };
      
    case CALL_STATUSES.EMERGENCY_PENDING:
      return {
        available: false,
        reason: 'emergency',
        message: 'User is handling an emergency call',
        canRetry: false,
      };
      
    default:
      return {
        available: false,
        reason: 'unknown',
        message: 'User status is unknown',
        canRetry: true,
      };
  }
};

/**
 * Format last seen timestamp for display
 * @param {string|number} lastSeen - Last seen timestamp
 * @returns {string} Formatted time string
 */
export const formatLastSeen = (lastSeen) => {
  if (!lastSeen) {return 'unknown time';}
  
  try {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) {return 'just now';}
    if (diffMinutes < 60) {return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;}
    if (diffHours < 24) {return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;}
    if (diffDays < 7) {return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;}
    
    return date.toLocaleDateString();
  } catch (error) {
    console.warn('Error formatting last seen:', error);
    return 'unknown time';
  }
};

/**
 * Get platform display info
 * @param {string} platform - Platform identifier
 * @param {string} appType - App type identifier
 * @returns {Object} Platform display information
 */
export const getPlatformInfo = (platform, appType) => {
  const platformKey = platform || appType || PLATFORMS.UNKNOWN;
  
  const platformMap = {
    [PLATFORMS.WEB_REACT]: {
      name: 'Web App',
      icon: '🌐',
      color: '#2196F3',
      shortName: 'Web',
    },
    [PLATFORMS.FLUTTER_ANDROID]: {
      name: 'Android App',
      icon: '🤖',
      color: '#4CAF50',
      shortName: 'Android',
    },
    [PLATFORMS.FLUTTER_IOS]: {
      name: 'iOS App (Flutter)',
      icon: '📱',
      color: '#FF9800',
      shortName: 'iOS Flutter',
    },
    [PLATFORMS.SWIFTUI_IOS]: {
      name: 'iOS App (Native)',
      icon: '🍎',
      color: '#9C27B0',
      shortName: 'iOS Native',
    },
    'web_browser': {
      name: 'Web Browser',
      icon: '🌐',
      color: '#2196F3',
      shortName: 'Web',
    },
    'android_flutter': {
      name: 'Android App',
      icon: '🤖',
      color: '#4CAF50',
      shortName: 'Android',
    },
    'ios_flutter': {
      name: 'iOS App (Flutter)',
      icon: '📱',
      color: '#FF9800',
      shortName: 'iOS Flutter',
    },
    'ios_swiftui': {
      name: 'iOS App (Native)',
      icon: '🍎',
      color: '#9C27B0',
      shortName: 'iOS Native',
    },
  };
  
  return platformMap[platformKey] || {
    name: 'Unknown Platform',
    icon: '❓',
    color: '#757575',
    shortName: 'Unknown',
  };
};

/**
 * Check version compatibility
 * @param {string} userVersion - User's app version
 * @param {string} minVersion - Minimum required version
 * @returns {Object} Compatibility status
 */
export const checkVersionCompatibility = (userVersion, minVersion = '1.0.0') => {
  if (!userVersion || userVersion === 'Unknown') {
    return {
      compatible: false,
      reason: 'unknown_version',
      message: 'App version is unknown',
      severity: 'warning',
    };
  }
  
  try {
    const parseVersion = (version) => {
      const parts = version.replace(/[^\d.]/g, '').split('.');
      return parts.map(part => parseInt(part) || 0);
    };
    
    const userParts = parseVersion(userVersion);
    const minParts = parseVersion(minVersion);
    
    for (let i = 0; i < Math.max(userParts.length, minParts.length); i++) {
      const userPart = userParts[i] || 0;
      const minPart = minParts[i] || 0;
      
      if (userPart > minPart) {
        return {
          compatible: true,
          reason: 'newer_version',
          message: 'App version is up to date',
          severity: 'success',
        };
      } else if (userPart < minPart) {
        return {
          compatible: false,
          reason: 'outdated_version',
          message: `App version ${userVersion} is outdated. Minimum required: ${minVersion}`,
          severity: 'error',
        };
      }
    }
    
    return {
      compatible: true,
      reason: 'exact_version',
      message: 'App version is compatible',
      severity: 'success',
    };
  } catch (error) {
    console.warn('Error checking version compatibility:', error);
    return {
      compatible: false,
      reason: 'version_parse_error',
      message: 'Unable to verify app version compatibility',
      severity: 'warning',
    };
  }
};

/**
 * Sanitize data for display (XSS prevention)
 * @param {string} data - Data to sanitize
 * @returns {string} Sanitized data
 */
export const sanitizeForDisplay = (data) => {
  if (typeof data !== 'string') {return String(data || '');}
  
  return data
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Get call status display info
 * @param {string} callStatus - Call status
 * @returns {Object} Status display information
 */
export const getCallStatusInfo = (callStatus) => {
  const statusMap = {
    [CALL_STATUSES.AVAILABLE]: {
      label: 'Available',
      color: '#4CAF50',
      icon: '🟢',
      canCall: true,
    },
    [CALL_STATUSES.BUSY]: {
      label: 'Busy',
      color: '#F44336',
      icon: '🔴',
      canCall: false,
    },
    [CALL_STATUSES.PENDING]: {
      label: 'Ringing',
      color: '#FF9800',
      icon: '🟡',
      canCall: false,
    },
    [CALL_STATUSES.ACTIVE]: {
      label: 'In Call',
      color: '#2196F3',
      icon: '📞',
      canCall: false,
    },
    [CALL_STATUSES.ENDED]: {
      label: 'Call Ended',
      color: '#757575',
      icon: '⚫',
      canCall: true,
    },
    [CALL_STATUSES.DECLINED]: {
      label: 'Declined',
      color: '#F44336',
      icon: '❌',
      canCall: true,
    },
    [CALL_STATUSES.REJECTED]: {
      label: 'Rejected',
      color: '#F44336',
      icon: '🚫',
      canCall: true,
    },
    [CALL_STATUSES.TIMEOUT]: {
      label: 'No Answer',
      color: '#FF9800',
      icon: '⏰',
      canCall: true,
    },
    [CALL_STATUSES.CANCELLED]: {
      label: 'Cancelled',
      color: '#757575',
      icon: '🚫',
      canCall: true,
    },
    [CALL_STATUSES.EMERGENCY_PENDING]: {
      label: 'Emergency',
      color: '#E91E63',
      icon: '🚨',
      canCall: false,
    },
    [CALL_STATUSES.EMERGENCY]: {
      label: 'Emergency Active',
      color: '#D32F2F',
      icon: '🆘',
      canCall: false,
    },
  };
  
  return statusMap[callStatus] || {
    label: 'Unknown',
    color: '#757575',
    icon: '❓',
    canCall: false,
  };
};

export default {
  normalizeUserData,
  getDefaultUserData,
  checkUserAvailability,
  formatLastSeen,
  getPlatformInfo,
  checkVersionCompatibility,
  sanitizeForDisplay,
  getCallStatusInfo,
  PLATFORMS,
  CALL_STATUSES,
  DISCONNECT_REASONS,
};
